import { useReducer, useRef } from 'react'
import effects from 'app/effects/effects'
import { z } from 'zod'
import axios from 'axios'
// import { sortLanguage } from 'commons/language';
import MessageAlert from 'app/components/MessageAlert'
import { useDispatch } from 'react-redux'
import { htmlToPlainText } from 'libs/text'
import { getCreditsRequest, getRequestId } from './api'
import { usePlace } from 'commons/usePlace'
import { isNil } from 'ramda'
import apiConfigs from 'app/configs/apiConfigs'

function reduce(state, action) {
  switch (action.type) {
    case 'open':
      return {
        ...state,
        step: 'opened',
        body: action.body,
        promptText: state.selectedText
          ? state.selectedText
          : state.promptText
            ? state.promptText
            : '',
      }

    case 'exist':
      return {
        ...state,
        isExistAiImagePreview: true,
      }
    case 'close-exist-confirm':
      return {
        ...state,
        isExistAiImagePreview: false,
      }
    case 'exist-confirm':
      return {
        ...initialState,
        isExistAiImagePreview: false,
      }

    case 'selected-text-update':
      return { ...state, selectedText: action.selectedText }

    case 'prompt-text-update':
      return { ...state, promptText: action.promptText }

    case 'generate-image-with-prompt-text':
      if (!state.promptText) return state
      return {
        ...state,
        isGenerating: true,
        error: undefined,
      }
    case 'cancel':
      return {
        ...state,
        isCancelAiImagePreview: true,
      }
    case 'cancel-generate-image':
      return {
        ...state,
        step: 'opened',
        error: undefined,
        isCancelAiImagePreview: false,
        isGenerating: false,
      }

    case 'cancel-close':
      return {
        ...state,
        isCancelAiImagePreview: false,
      }
    case 'image-response': {
      return {
        ...state,
        isGenerating: false,
        image: action.response.map((image) => ({
          src: image.url
            ? image.url
            : `data:image/png;base64,${image.b64_json}`,
        }))[0],
      }
    }

    case 'show-error':
      return {
        ...state,
        isGenerating: false,
        error: action.error,
      }

    case 'select-language':
      return {
        ...state,
        topics: undefined,
        language: action.language,
      }

    case 'image-style-change':
      return { ...state, imageStyle: action.imageStyle }

    case 'image-orientation-change':
      return { ...state, imageOrientation: action.imageOrientation }

    case 'keywords-start': {
      return {
        ...state,
        isKeywordsGenerating: true,
      }
    }

    case 'keywords-response': {
      return {
        ...state,
        isKeywordsGenerating: false,
        promptText: action.response,
      }
    }

    case 'credits-response': {
      return {
        ...state,
        credits: action.credits,
      }
    }

    case 'recent-image-response': {
      return {
        ...state,
        recentImages: action.response,
      }
    }

    case 'tab-change': {
      return {
        ...state,
        tabIndex: action.tabIndex,
      }
    }

    case 'recent-image-select-change': {
      const selectedRecentImages = new Map(state.selectedRecentImages)
      if (action.isSelected) {
        selectedRecentImages.set(action.url, action.url)
      } else {
        selectedRecentImages.delete(action.url)
      }
      return {
        ...state,
        selectedRecentImages,
      }
    }
    case 'clear-body': {
      return {
        ...state,
        body: undefined,
      }
    }

    case 'close':
      return {
        ...initialState,
        image: state.image,
        recentImages: state.recentImages,
        promptText: state.promptText,
        imageStyle: state.imageStyle,
        imageOrientation: state.imageOrientation,
        credits: state.credits,
      }

    default:
      return state
  }
}

const initialState = {
  step: undefined,
  tabIndex: 0,
  selectedText: '',
  promptText: '',
  isGenerating: false,
  error: undefined,
  image: undefined,
  recentImages: undefined,
  selectedRecentImages: new Map(),
  topics: undefined,
  selectedTopic: undefined,
  selectedMesageLength: undefined,
  language: undefined,
  body: undefined,
  imageStyle: undefined,
  imageOrientation: undefined,
  credits: undefined,
  isExistAiImagePreview: false,
  isCancelAiImagePreview: false,
}

const imageResponseSchema = z.object({
  response: z.array(
    z
      .object({ url: z.string().optional() })
      .or(z.object({ b64_json: z.string().optional() })),
  ),
})

const keywordsResponseSchema = z.object({
  response: z.array(z.string().min(1)),
})

const recentImageSchema = z.object({
  response: z.array(
    z.object({
      url: z.string(),
      created_at: z.string(),
    }),
  ),
})

export function useGenerateImageWithAI(requestId, onInsertImage) {
  const reduxDispatch = useDispatch()
  const [state, dispatch] = useReducer(reduce, initialState)
  const cancellableRef = useRef(new AbortController())
  const place = usePlace()
  const isChanging = place.aiBillingCharged

  return {
    ...state,
    isOpen: state.step !== undefined,
    isSummarizeEnabled: state.body !== undefined && state.body.length > 0,
    isUseButtonDisabled:
      state.isGenerating ||
      (state.tabIndex === 0
        ? !state.image
        : state.tabIndex === 1
          ? state.selectedRecentImages.size === 0
          : true),
    // languages: languageData.items,
    onOpenClick: async function (body) {
      dispatch({ type: 'open', body })

      cancelInflightRequest(cancellableRef)

      await Promise.all([
        state.recentImages
          ? Promise.resolve()
          : listRecentImages(dispatch, cancellableRef),
        getCredits(dispatch, cancellableRef),
      ])
    },
    onCloseClick: function () {
      if (
        (isChanging && state.isGenerating) ||
        (isChanging && !isNil(state.image))
      ) {
        dispatch({ type: 'exist' })
      } else {
        cancelInflightRequest(cancellableRef)
        dispatch({ type: 'exist-confirm' })
      }
    },
    onExistImageConfirmClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'exist-confirm' })
    },
    onCloseExistConfirm: function () {
      dispatch({ type: 'close-exist-confirm' })
    },
    onGenerateImageWithPromptClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'generate-image-with-prompt-text' })
      generateImage(
        state.promptText,
        state.imageOrientation,
        state.imageStyle,
        requestId,
        dispatch,
        reduxDispatch,
        cancellableRef,
      )
    },
    onCancelGenerateImageClick: function () {
      if (isChanging) {
        dispatch({ type: 'cancel' })
      } else {
        cancelInflightRequest(cancellableRef)
        dispatch({ type: 'cancel-generate-image' })
      }
    },
    onCancelGenerateImageConfirmClick: function () {
      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'cancel-generate-image' })
    },
    onCloseCancelGenerateImage: function () {
      dispatch({ type: 'cancel-close' })
    },

    onSelectedTextChange: function (selectedText) {
      dispatch({ type: 'selected-text-update', selectedText })
    },
    onPromptTextChange: function (promptText) {
      dispatch({ type: 'prompt-text-update', promptText })
    },
    onImageStyleChange: function (imageStyle) {
      dispatch({ type: 'image-style-change', imageStyle })
    },
    onImageOrientationChange: function (imageOrientation) {
      dispatch({ type: 'image-orientation-change', imageOrientation })
    },
    onSummarizeClick: async function () {
      cancelInflightRequest(cancellableRef)
      await generateKeywords(
        state.body,
        requestId,
        dispatch,
        reduxDispatch,
        cancellableRef,
      )
    },
    onTabChange: function (tabIndex) {
      dispatch({ type: 'tab-change', tabIndex })
    },
    onRecentImageSelectChange: function (isSelected, url) {
      dispatch({ type: 'recent-image-select-change', isSelected, url })
    },
    onUseImageClick: function () {
      if (state.tabIndex === 0 && state.image)
        onInsertImage(state.image.src, state.promptText)

      if (state.tabIndex === 1 && state.selectedRecentImages.size > 0)
        for (const url of state.selectedRecentImages.values()) {
          onInsertImage(url, state.promptText)
        }

      cancelInflightRequest(cancellableRef)
      dispatch({ type: 'close' })
    },
  }
}

async function generateImageRequest(
  prompt,
  aspect_ratio,
  style,
  requestId,
  cancellableRef,
) {
  return await effects.apis.ai.image.generate({
    data: {
      text: prompt,
      aspect_ratio,
      style,
      numberOfImages: 1,
      // responseFormat: 'b64_json',
      format: 'url',
      request_id: requestId,
    },
    signal: cancellableRef.current.signal,
  })
}

async function generateImage(
  prompt,
  aspect_ratio,
  style,
  requestId,
  dispatch,
  reduxDispatch,
  cancellableRef,
) {
  try {
    const { response } = imageResponseSchema.parse(
      await generateImageRequest(
        prompt,
        aspect_ratio,
        style,
        requestId,
        cancellableRef,
      ),
    )
    dispatch({ type: 'image-response', response })
    await Promise.all([
      getCredits(dispatch, cancellableRef),
      listRecentImages(dispatch, cancellableRef),
    ])
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
    reduxDispatch(
      MessageAlert.showError({
        message:
          'Oh no! It seems there was an error loading the content. Please refresh or try again later.',
        duration: null,
      }),
    )
  }
}

async function listRecentImages(dispatch, reduxDispatch, cancellableRef) {
  try {
    const { response } = recentImageSchema.parse(
      await effects.apis.ai.image.recent({
        data: {
          limit: 10,
        },
        signal: cancellableRef,
      }),
    )
    dispatch({ type: 'recent-image-response', response })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
    reduxDispatch(
      MessageAlert.showError({
        message:
          'Oh no! It seems there was an error loading the content. Please refresh or try again later.',
        duration: null,
      }),
    )
  }
}

async function generateKeywords(
  text,
  requestId,
  dispatch,
  reduxDispatch,
  cancellableRef,
) {
  try {
    if (!text) return
    dispatch({ type: 'keywords-start' })
    const { response } = keywordsResponseSchema.parse(
      await effects.apis.ai.keywords({
        data: {
          text,
          request_id: requestId,
        },
        signal: cancellableRef.current.signal,
      }),
    )
    dispatch({
      type: 'keywords-response',
      response: htmlToPlainText(response.join(' ')),
    })
  } catch (error) {
    if (axios.isCancel(error)) return
    dispatch({ type: 'show-error', error })
    reduxDispatch(
      MessageAlert.showError({
        message:
          'Oh no! It seems there was an error loading the content. Please refresh or try again later.',
        duration: null,
      }),
    )
  }
}

async function getCredits(dispatch, cancellableRef) {
  try {
    const response = await getCreditsRequest(cancellableRef)
    dispatch({
      type: 'credits-response',
      credits: response.credits,
    })
  } catch (error) {
    if (axios.isCancel(error)) return
  }
}

function cancelInflightRequest(cancellableRef) {
  cancellableRef.current.abort()
  cancellableRef.current = new AbortController()
}
